import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { DashboardHeader } from './DashboardHeader';
import { DashboardGrid } from './DashboardGrid';
import { DashboardEmptyState } from './DashboardEmptyState';
import { DashboardSidebar } from '../DashboardSidebar/DashboardSidebar';
import { useDashboardUIStore } from '../../stores/dashboardUIStore';
import { useDashboard, useUpdateCardPositions } from '../../hooks/useDashboardData';
import { useDashboards, useDeleteCard } from '../../hooks/useSavedCards';
import type { GridLayoutItem, CardSizePreset } from '../../types/dashboard.types';
import { getSizePresetDimensions } from '../../types/dashboard.types';

interface ConfigurableDashboardProps {
  dashboardId?: string;
}

export function ConfigurableDashboard({ dashboardId }: ConfigurableDashboardProps) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const { data: dashboards, isLoading: isDashboardsLoading } = useDashboards();
  const updateCardPositions = useUpdateCardPositions();
  const deleteCard = useDeleteCard();

  const {
    activeDashboardId,
    setActiveDashboardId,
    isEditMode,
    setEditMode,
    toggleEditMode,
    isSidebarOpen,
    setSidebarOpen,
    toggleSidebar,
    pendingLayoutChanges,
    setPendingLayoutChanges,
    selectedCardId,
    setSelectedCardId,
    discardLayoutChanges,
  } = useDashboardUIStore();

  const effectiveDashboardId = dashboardId || activeDashboardId;
  const { data: dashboard, isLoading: isDashboardLoading } = useDashboard(effectiveDashboardId);

  useEffect(() => {
    if (!isDashboardsLoading && dashboards && dashboards.length > 0) {
      if (!effectiveDashboardId) {
        const defaultDashboard = dashboards.find((d) => d.isDefault) || dashboards[0];
        setActiveDashboardId(defaultDashboard.id);
        navigate(`/dashboard/${defaultDashboard.id}`, { replace: true });
      } else if (dashboardId && dashboardId !== activeDashboardId) {
        setActiveDashboardId(dashboardId);
      }
    }
  }, [
    isDashboardsLoading,
    dashboards,
    dashboardId,
    effectiveDashboardId,
    activeDashboardId,
    setActiveDashboardId,
    navigate,
  ]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const handleDashboardSelect = useCallback(
    (id: string) => {
      if (pendingLayoutChanges) {
        if (!window.confirm('You have unsaved changes. Discard and switch dashboards?')) {
          return;
        }
        discardLayoutChanges();
      }
      setEditMode(false);
      setActiveDashboardId(id);
      navigate(`/dashboard/${id}`);
    },
    [pendingLayoutChanges, discardLayoutChanges, setEditMode, setActiveDashboardId, navigate]
  );

  const handleLayoutChange = useCallback(
    (layout: GridLayoutItem[]) => {
      setPendingLayoutChanges(layout);
    },
    [setPendingLayoutChanges]
  );

  const handleSaveLayout = useCallback(async () => {
    if (!effectiveDashboardId || !pendingLayoutChanges) return;

    const positions = pendingLayoutChanges.map((item) => ({
      cardId: item.i,
      position: { x: item.x, y: item.y, w: item.w, h: item.h },
    }));

    try {
      await updateCardPositions.mutateAsync({
        dashboardId: effectiveDashboardId,
        positions,
      });
      setPendingLayoutChanges(null);
    } catch (error) {
      console.error('Failed to save layout:', error);
    }
  }, [effectiveDashboardId, pendingLayoutChanges, updateCardPositions, setPendingLayoutChanges]);

  const handleDiscardLayout = useCallback(() => {
    discardLayoutChanges();
  }, [discardLayoutChanges]);

  const handleToggleEditMode = useCallback(() => {
    if (isEditMode && pendingLayoutChanges) {
      if (!window.confirm('You have unsaved changes. Discard?')) {
        return;
      }
      discardLayoutChanges();
    }
    toggleEditMode();
    setSelectedCardId(null);
  }, [isEditMode, pendingLayoutChanges, discardLayoutChanges, toggleEditMode, setSelectedCardId]);

  const handleCardDelete = useCallback(
    async (cardId: string) => {
      if (!window.confirm('Remove this card from the dashboard?')) return;
      try {
        await deleteCard.mutateAsync(cardId);
      } catch (error) {
        console.error('Failed to delete card:', error);
      }
    },
    [deleteCard]
  );

  const handleCardSizeChange = useCallback(
    (cardId: string, preset: CardSizePreset) => {
      if (!pendingLayoutChanges && dashboard?.cards) {
        const currentLayout = dashboard.cards.map((card) => ({
          i: card.id,
          x: card.position.x,
          y: card.position.y,
          w: card.position.w,
          h: card.position.h,
        }));

        const dimensions = getSizePresetDimensions(preset);
        const newLayout = currentLayout.map((item) =>
          item.i === cardId ? { ...item, w: dimensions.w, h: dimensions.h } : item
        );

        setPendingLayoutChanges(newLayout);
      }
    },
    [dashboard?.cards, pendingLayoutChanges, setPendingLayoutChanges]
  );

  const isLoading = isDashboardsLoading || isDashboardLoading;

  const cards = dashboard?.cards || [];
  const displayCards = pendingLayoutChanges
    ? cards.map((card) => {
        const layoutItem = pendingLayoutChanges.find((l) => l.i === card.id);
        if (layoutItem) {
          return {
            ...card,
            position: { x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h },
          };
        }
        return card;
      })
    : cards;

  return (
    <div className="flex flex-col h-full">
      <DashboardHeader
        dashboard={dashboard ?? null}
        isEditMode={isEditMode}
        hasUnsavedChanges={!!pendingLayoutChanges}
        onToggleSidebar={toggleSidebar}
        onToggleEditMode={handleToggleEditMode}
        onSaveLayout={handleSaveLayout}
        onDiscardLayout={handleDiscardLayout}
      />

      <div ref={containerRef} className="flex-1 min-w-0 overflow-auto px-4 pb-4 pt-4 md:px-8 md:pb-8">
        {isLoading ? (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <Skeleton className="h-48 w-full" />
              </div>
            ))}
          </div>
        ) : displayCards.length === 0 ? (
          <DashboardEmptyState />
        ) : containerWidth > 0 ? (
          <DashboardGrid
            cards={displayCards}
            isEditMode={isEditMode}
            selectedCardId={selectedCardId}
            containerWidth={containerWidth}
            onLayoutChange={handleLayoutChange}
            onCardSelect={setSelectedCardId}
            onCardDelete={handleCardDelete}
            onCardSizeChange={handleCardSizeChange}
          />
        ) : null}
      </div>

      <DashboardSidebar
        open={isSidebarOpen}
        onOpenChange={setSidebarOpen}
        activeDashboardId={effectiveDashboardId}
        onDashboardSelect={handleDashboardSelect}
      />
    </div>
  );
}
